export type Occasion = {
  slug: string;
  name: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  intro: string;
  hero: string;
  pillars: { title: string; text: string }[];
  faq: { q: string; a: string }[];
  /** Réponse directe placée en tête de page (format AI Overview). */
  answer?: string;
  /** Sections rédigées propres à l'occasion. */
  sections?: { h2: string; paragraphs: string[] }[];
  /** Liens internes contextuels affichés en fin de page. */
  relatedLinks?: { to: string; params?: Record<string, string>; label: string }[];
};


export const OCCASIONS: Record<string, Occasion> = {
  anniversaire: {
    slug: "anniversaire",
    name: "Anniversaire",
    metaTitle: "DJ anniversaire Vaucluse & Avignon | DJ Val",
    metaDescription:
      "DJ professionnel pour vos anniversaires : 30, 40, 50, 60 ans, surprise party. Sono, lumière, animation sur mesure dans le Vaucluse et Avignon.",
    h1: "DJ pour Anniversaire",
    intro:
      "30, 40, 50, 60 ans ou une simple envie de faire la fête : un DJ pro change tout. Du mix au micro animation, je transforme votre soirée d'anniversaire en moment inoubliable.",
    hero: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1200&q=72&auto=format&fit=crop",
    pillars: [
      { title: "Toutes générations", text: "Variété française, 80's-2000's, hits actuels — je lis votre dancefloor et adapte chaque transition." },
      { title: "Forfait clé en main", text: "Sono, éclairage LED, micros, installation et démontage compris. Vous n'avez à vous occuper de rien." },
      { title: "Surprise & émotion", text: "Diaporama, intro personnalisée, dédicaces : on peut tout préparer ensemble pour marquer le coup." },
    ],
    faq: [
      { q: "Quel tarif pour un anniversaire ?", a: "À partir de 600 € pour 4 h, sono + lumière incluses. Devis personnalisé selon durée et matériel." },
      { q: "Vous animez les anniversaires d'enfants / ados ?", a: "Oui, avec un répertoire adapté et une animation interactive (karaoké, jeux dancefloor)." },
    ],
  },
  baptemes: {
    slug: "baptemes",
    name: "Baptêmes & Communions",
    metaTitle: "DJ baptême & communion Vaucluse – Animation musicale | DJ Val",
    metaDescription:
      "DJ pour baptême et communion dans le Vaucluse : animation musicale intergénérationnelle, sono discrète, micro pour les discours. Devis en 24 h, 20 ans d'expérience.",
    h1: "DJ pour baptême et communion dans le Vaucluse",
    intro:
      "Un baptême ou une communion réunit plusieurs générations dans une atmosphère douce et joyeuse. Mon rôle : créer le bon climat sonore, du cocktail au gâteau jusqu'à la piste.",
    answer:
      "Un DJ pour un baptême, c'est d'abord quelqu'un qui gère le volume : une musique d'ambiance pendant l'arrivée des invités et le repas, un micro pour les discours du parrain et de la marraine, puis une vraie piste de danse en fin de journée. DJ Val intervient dans tout le Vaucluse, avec un devis transmis en 24 heures.",
    hero: "https://images.unsplash.com/photo-1530023367847-a683933f4172?w=1200&q=72&auto=format&fit=crop",
    pillars: [
      { title: "Ambiance familiale", text: "Sélection respectueuse, volume maîtrisé pour les enfants et les aînés, énergie quand il faut." },
      { title: "Cocktail soigné", text: "Playlist lounge ou chanson française pendant le vin d'honneur, transition fluide vers le repas." },
      { title: "Diaporama souvenirs", text: "Possibilité de projeter un diaporama photos préparé par vos soins pendant le dessert." },
    ],
    sections: [
      {
        h2: "Le déroulé type d'un baptême ou d'une communion",
        paragraphs: [
          "L'animation musicale d'un baptême commence bien avant la piste de danse. À l'arrivée des invités, la musique doit exister sans s'imposer : un fond sonore posé, diffusé à volume conversationnel, qui installe l'ambiance pendant que les familles se retrouvent. C'est aussi le moment où je vérifie l'implantation avec le lieu et où je teste les micros.",
          "Pendant le repas, l'animation musicale d'une communion suit le rythme du service. La musique reste discrète entre les plats, monte légèrement au moment du dessert, et s'efface complètement dès qu'un discours commence. Parrain, marraine, grands-parents, parents : chacun dispose d'un micro sans fil, sans avoir à se lever de table ni à crier pour être entendu.",
          "Vient ensuite le temps des animations. Pour les enfants, des jeux musicaux courts fonctionnent très bien : chaises musicales, statue musicale, mini-karaoké sur des titres qu'ils connaissent. Pour les adultes, la bascule se fait naturellement en fin d'après-midi ou en début de soirée, avec un répertoire qui parle à tout le monde.",
          "La playlist d'un baptême est par définition intergénérationnelle : chanson française, variété des années 60 à 2000, quelques titres actuels pour les adolescents, et des morceaux plus doux pour les moments d'émotion. Vous m'envoyez vos incontournables et vos interdits, je construis le reste et j'ajuste en direct selon la salle.",
        ],
      },
      {
        h2: "Un matériel adapté à une fête de famille",
        paragraphs: [
          "Un DJ baptême n'installe pas la même chose qu'un DJ de mariage. La sonorisation reste volontairement discrète : des enceintes de taille contenue, posées sur pied, orientées vers la zone de fête et non vers les tables, avec des basses tenues pour ne pas gêner les plus jeunes enfants ni les personnes âgées.",
          "Deux micros sans fil sont fournis pour les discours et les prises de parole, avec un réglage fait à l'avance pour éviter tout effet de larsen. Si vous souhaitez projeter un diaporama de photos, le vidéoprojecteur et l'écran s'ajoutent à la prestation.",
          "Côté lumière, l'approche est la même : un éclairage doux et chaleureux pendant le repas, puis une mise en lumière plus marquée sur la zone de danse quand la fête bascule. Rien de stroboscopique tant que des enfants en bas âge sont présents.",
        ],
      },
      {
        h2: "Tarifs et zone d'intervention",
        paragraphs: [
          "Une animation musicale de baptême ou de communion se situe le plus souvent entre 690 € et 990 €, pour une prestation de quatre à six heures comprenant le déplacement, l'installation, la sonorisation, les micros, l'éclairage et le démontage. Le montant exact dépend de la durée, du lieu et des options comme le diaporama ou le photobooth. Le devis est transmis sous 24 heures, sans engagement.",
          "Basé à Cavaillon, j'interviens sur Avignon, Cavaillon, Carpentras, L'Isle-sur-la-Sorgue, Orange et Apt, ainsi que dans l'ensemble du Vaucluse, du Luberon et des Alpilles. Le déplacement est compris dans le tarif annoncé sur cette zone.",
          "L'activité est déclarée à la SACEM, assurée en responsabilité civile professionnelle chez Hiscox et dispose d'un SIRET : les salles municipales et les domaines qui exigent ces documents les reçoivent avant la date.",
        ],
      },
    ],
    relatedLinks: [
      { to: "/dj-anniversaire/$ville", params: { ville: "avignon" }, label: "DJ anniversaire à Avignon" },
      { to: "/location-materiel", label: "Location de matériel son et lumière" },
      { to: "/location-photobooth-avignon", label: "Location de photobooth à Avignon" },
      { to: "/dj-vaucluse", label: "DJ dans le Vaucluse" },
    ],
    faq: [
      { q: "Quelle durée prévoir pour un DJ de baptême ?", a: "Le plus souvent quatre à six heures : arrivée des invités, repas, animations et fin de journée dansante. La durée est modulable et se cale sur l'horaire du traiteur." },
      { q: "Le volume est-il adapté aux enfants et aux personnes âgées ?", a: "Oui. Les enceintes sont orientées vers la zone de fête, les basses sont tenues et le volume reste conversationnel pendant tout le repas. Il ne monte que sur la zone de danse, en fin de journée." },
      { q: "Des animations sont-elles incluses pour les enfants ?", a: "Oui : jeux musicaux courts, chaises musicales, mini-karaoké sur des titres qu'ils connaissent. Ces séquences sont préparées avec vous et durent le temps qu'il faut, pas plus." },
      { q: "Peut-on organiser un baptême en extérieur ?", a: "Oui, à condition de disposer d'une prise de courant et d'un abri pour le matériel en cas d'averse ou de forte chaleur. Une solution de repli à l'intérieur est toujours prévue en amont." },
      { q: "Combien de temps à l'avance faut-il réserver ?", a: "Quatre à huit semaines suffisent généralement pour un baptême ou une communion. Les dimanches de mai et juin, période des communions, se remplissent plus tôt : mieux vaut compter trois mois." },
      { q: "Faut-il verser un acompte ?", a: "Oui, un acompte est demandé à la signature du devis pour bloquer la date, le solde étant réglé le jour de la prestation. Le détail figure noir sur blanc dans le devis." },
    ],
  },

  entreprise: {
    slug: "entreprise",
    name: "Soirée d'Entreprise & CE",
    metaTitle: "DJ soirée entreprise & CE Vaucluse | DJ Val",
    metaDescription:
      "DJ professionnel pour soirées d'entreprise, comité d'entreprise, séminaires, gala. Devis sur mesure pour Vaucluse, Bouches-du-Rhône, Gard.",
    h1: "DJ Soirée d'Entreprise & Comité d'Entreprise",
    intro:
      "Gala annuel, kick-off, team building, soirée CE : votre événement professionnel mérite une animation impeccable. Image soignée, fiabilité totale, facturation pro.",
    hero: "/__l5e/assets-v1/4d74256c-5848-445d-b55c-760c4d889589/soiree-entreprise.webp",
    pillars: [
      { title: "Pro à 100%", text: "Devis, facture, SACEM, assurance RC pro. Tout est en règle." },
      { title: "Adaptable à votre brief", text: "Soirée années 80, ambiance club, cocktail jazzy : on cale le ton selon votre culture d'entreprise." },
      { title: "Matériel scalable", text: "De 30 à 800 personnes : système son et lumière dimensionné à votre salle." },
    ],
    faq: [
      { q: "Pouvez-vous facturer en société ?", a: "Oui, SIRET, TVA, facture conforme. Paiement à 30 jours possible pour les grands comptes." },
      { q: "Intervenez-vous pour les conventions / séminaires ?", a: "Oui : sono conférence, micros HF, gestion technique du contenu vidéo." },
    ],
  },
  mairie: {
    slug: "mairie",
    name: "Événement Communal",
    metaTitle: "DJ Mairie & Événement Communal Vaucluse | DJ Val",
    metaDescription:
      "Prestation DJ pour fêtes votives, bals communaux, 14 juillet, fêtes de village. Devis adapté aux collectivités du Vaucluse et Bouches-du-Rhône.",
    h1: "DJ pour Mairie & Événements Communaux",
    intro:
      "Bals populaires, fêtes votives, 14 juillet, fête de la musique : j'accompagne les communes et associations avec un matériel professionnel et une facturation adaptée aux collectivités.",
    hero: "https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=1200&q=72&auto=format&fit=crop",
    pillars: [
      { title: "Marchés publics OK", text: "Devis détaillés, attestations d'assurance, déclaration SACEM gérée pour la commune." },
      { title: "Grande puissance sono", text: "Systèmes line-array pour 500 à 2000 personnes en extérieur (place de village, parc municipal)." },
      { title: "Tout public", text: "Du retraité au gamin : variété française, musette, hits internationaux, génériques télé cultes." },
    ],
    faq: [
      { q: "Avez-vous une assurance RC pro adaptée ?", a: "Oui, attestation transmise sur demande, montant garantie adapté aux événements publics." },
      { q: "Pouvez-vous fournir la SACEM clé en main ?", a: "Conseil et accompagnement pour la déclaration SACEM par la commune, gratuit." },
    ],
  },
  "soiree-privee": {
    slug: "soiree-privee",
    name: "Soirée Privée",
    metaTitle: "DJ soirée privée Provence & Vaucluse | DJ Val",
    metaDescription:
      "DJ pour soirée privée à domicile, villa, restaurant. Avignon, Cavaillon, Aix-en-Provence. Devis transparent, matériel professionnel.",
    h1: "DJ pour Soirée Privée",
    intro:
      "Une fête à la maison, un dîner chez vous, une soirée piscine en villa : un DJ pro fait la différence. Installation discrète, son maîtrisé, ambiance ciselée à vos goûts.",
    hero: "/__l5e/assets-v1/2eb0148a-e9f4-44c0-983e-241177bbf454/soiree-privee.webp",
    pillars: [
      { title: "Discrétion", text: "Tenue élégante, installation propre, gestion du volume pour respecter le voisinage." },
      { title: "Sur mesure", text: "Brief préalable, playlist préparée, sélection live selon l'énergie de vos invités." },
      { title: "Forfait court ou long", text: "De 3 h à toute la nuit. Pas de minimum imposé, devis adapté à votre besoin." },
    ],
    faq: [
      { q: "Acceptez-vous les petites jauges (20-30 pers) ?", a: "Oui, j'ai un setup compact dédié aux petites soirées intimes." },
      { q: "Pouvez-vous installer en piscine / terrasse ?", a: "Oui, sécurisation des câbles, matériel protégé, plan B en cas de pluie." },
    ],
  },
  "saint-sylvestre": {
    slug: "saint-sylvestre",
    name: "Saint-Sylvestre / Jour de l'an",
    metaTitle: "DJ Saint-Sylvestre Vaucluse : réveillon | DJ Val",
    metaDescription:
      "DJ professionnel pour votre soirée du nouvel an. Réveillon Saint-Sylvestre dans le Vaucluse, Bouches-du-Rhône. Devis fin d'année.",
    h1: "DJ Saint-Sylvestre & Réveillon du Nouvel An",
    intro:
      "Le 31 décembre, on ne se rate pas. Un DJ pro pour passer minuit en beauté, faire danser tout le monde et finir l'année en feu d'artifice musical.",
    hero: "/__l5e/assets-v1/e9b8f018-5a2e-49bf-b6f9-4fb2b4401a08/saint-sylvestre.webp",
    pillars: [
      { title: "Compte à rebours soigné", text: "Montée musicale, son et lumière calibrés pour le moment du décompte." },
      { title: "Réservation anticipée", text: "Date hautement demandée — bloquez votre 31 décembre dès septembre / octobre." },
      { title: "Sono + lumière premium", text: "Forfait nouvel an avec lyres, lasers, machine à fumée pour un effet club." },
    ],
    faq: [
      { q: "Vous restez jusqu'à quelle heure le 31 ?", a: "Jusqu'à 4-5 h du matin si vous le souhaitez. Tarif nuit clair, sans surprise." },
      { q: "Prestation pour restaurant / club privé ?", a: "Oui, je collabore avec plusieurs lieux du Vaucluse pour leur soirée nouvel an." },
    ],
  },
  camping: {
    slug: "camping",
    name: "Camping & Plein Air",
    metaTitle: "DJ camping Provence : animation | DJ Val",
    metaDescription:
      "DJ pour campings, hôtels et villages vacances en Provence. Soirées dansantes, animations estivales, sono extérieure adaptée.",
    h1: "DJ pour Camping & Village Vacances",
    intro:
      "Soirée mousse, bal des vacanciers, karaoké en plein air : j'anime les campings et villages vacances de Provence avec un matériel adapté à l'extérieur et aux contraintes des établissements.",
    hero: "/__l5e/assets-v1/da22d837-7110-4c0d-b667-586684ddcc3b/camping.webp",
    pillars: [
      { title: "Multi-public international", text: "Français, anglais, néerlandais, allemand : sélection internationale qui fédère." },
      { title: "Forfait saison", text: "Tarif dégressif pour engagement sur plusieurs soirées de la saison estivale." },
      { title: "Soirées thématiques", text: "Mousse, fluo, années 80, blanche : packs thématiques disponibles." },
    ],
    faq: [
      { q: "Travaillez-vous en saison complète ?", a: "Oui, planning saisonnier juin-septembre établi en début d'année." },
      { q: "Matériel adapté plein air ?", a: "Oui, enceintes amplifiées résistantes à l'humidité, éclairages IP étanches en option." },
    ],
  },
};

export const OCCASION_SLUGS = Object.keys(OCCASIONS);
